import { FC, useState } from "react";
import Chart from "react-apexcharts";
import { useTranslation } from "react-i18next";
import {
  Box,
  MenuItem,
  Skeleton,
  Typography,
  styled,
  useMediaQuery,
  useTheme
} from "@mui/material";
import PageSection from "components/PageSection/PageSection";
import SelectField from "components/SelectField";
import { ApexOptions } from "apexcharts";
import { primaryDefaultColors } from "styles/theme";

const Container = styled(Box)(({ theme }) => ({
  minHeight: 464,
  display: "flex",
  padding: "16px",
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${theme.palette.grey["200"]}`,
  backgroundColor: theme.palette.background.default
}));

export type HeatmapData = {
  name: string | undefined;
  data: {
    x: string;
    y: number | null;
  }[];
}[];

type Props = {
  dayData: HeatmapData;
  hourData: HeatmapData;
  isFetching: boolean;
};

const CapturePointHeatmap: FC<Props> = ({ dayData, hourData, isFetching }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [isByHour, setIsByHour] = useState<string>("hour");
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const currentData = isByHour === "hour" ? hourData : dayData;
  const maxValue = currentData.reduce(
    (max, serie) =>
      serie.data.reduce(
        (serieMax, point) => Math.max(serieMax, point.y ?? 0),
        max
      ),
    0
  );

  // Altura proporcional ao numero de locais: com muitos pontos a altura fixa espremia as linhas.
  const ROW_HEIGHT = 26;
  const CHART_CHROME_HEIGHT = 120; // legenda + eixo X
  const chartHeight = Math.max(
    432,
    currentData.length * ROW_HEIGHT + CHART_CHROME_HEIGHT
  );

  // Faixas relativas ao maior valor: zero cinza, ate 20% baixa, ate 80% media, acima disso alta.
  // Os limites nao podem se sobrepor: o ApexCharts aplica a ULTIMA faixa que casa, nao a primeira.
  const lowMax = Math.floor(maxValue * 0.2);
  const mediumMax = Math.floor(maxValue * 0.8);
  const colorRanges = [
    {
      from: 0,
      to: 0,
      name: t("MonitoredVehiclesDashboardPage.heatmapTooltipNone"),
      color: "#E0E0E0"
    },
    {
      from: 1,
      to: lowMax,
      name: t("MonitoredVehiclesDashboardPage.heatmapTooltipLow"),
      color: "#FF0000"
    },
    {
      from: lowMax + 1,
      to: mediumMax,
      name: t("MonitoredVehiclesDashboardPage.heatmapTooltipMedium"),
      color: "#FFB200"
    },
    {
      from: mediumMax + 1,
      to: Infinity,
      name: t("MonitoredVehiclesDashboardPage.heatmapTooltipHigh"),
      color: "#00A100"
    }
  ];

  const options: ApexOptions = {
    chart: {
      type: "heatmap",
      toolbar: {
        show: true,
        tools: {
          download: false,
          selection: false,
          zoom: false,
          zoomin: false,
          zoomout: false,
          pan: false,
          reset: false
        }
      }
    },
    plotOptions: {
      heatmap: {
        enableShades: false,
        colorScale: {
          ranges: colorRanges
        }
      }
    },
    xaxis: {
      labels: {
        show: true,
        rotate: -45,
        rotateAlways: true
      }
    },
    yaxis: {
      labels: {
        show: !isMobile
      }
    }
  };

  return (
    <PageSection
      title={t("MonitoredVehiclesDashboardPage.camPerformace")}
      variant="h5"
      actions={
        <SelectField
          size="small"
          style={{
            marginBottom: 0
          }}
          value={isByHour}
          disabled={isFetching}
          onChange={event => {
            setIsByHour(event.target.value as string);
          }}
        >
          <MenuItem value="hour">
            {t("MonitoredVehiclesDashboardPage.byHour")}
          </MenuItem>
          <MenuItem value="day">
            {t("MonitoredVehiclesDashboardPage.byDay")}
          </MenuItem>
        </SelectField>
      }
    >
      {isFetching && <Skeleton variant="rectangular" height={348} />}
      {!isFetching && hourData.length > 0 && dayData.length > 0 && (
        <Container sx={{ height: chartHeight + 32 }}>
          <div
            style={{
              width: "100%"
            }}
          >
            <Chart
              height={chartHeight}
              type="heatmap"
              options={options}
              series={currentData}
            />
          </div>
        </Container>
      )}
      {!isFetching && hourData.length === 0 && dayData.length === 0 && (
        <Container sx={{ alignItems: "center", justifyContent: "center" }}>
          <Typography variant="h3" color={primaryDefaultColors.light}>
            {t("MonitoredVehiclesDashboardPage.emptyHeatmap")}
          </Typography>
        </Container>
      )}
    </PageSection>
  );
};

export default CapturePointHeatmap;
